import React, { useEffect, useState, useMemo, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Upload, Search, Download, FileText, History, Database, AlertTriangle, X, ShieldCheck, Trash2, CalendarPlus, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const PAGE_SIZE = 50;

export default function LegacyLeadsPage() {
  const { axiosAuth, user, API } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const canConvert = ["super_admin", "admin", "recepcion", "jefe_taller"].includes(user?.role);

  // Listado
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Filtros
  const [q, setQ] = useState("");
  const [phone, setPhone] = useState("");
  const [plate, setPlate] = useState("");
  const [formName, setFormName] = useState("");
  const [consent, setConsent] = useState("any");
  const [forms, setForms] = useState([]);
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");

  // Wizard
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(1); // 1 select, 2 preview, 3 confirm
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [sampleOnly, setSampleOnly] = useState(true);
  const [sampleSize, setSampleSize] = useState(20);
  const [confirmText, setConfirmText] = useState("");
  const fileInputRef = useRef(null);

  // Historial
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteText, setDeleteText] = useState("");

  // Detalle
  const [detail, setDetail] = useState(null);

  // Conversión a cita (Iter 163)
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertLead, setConvertLead] = useState(null);
  const [convertPrefill, setConvertPrefill] = useState(null);  // {prefill, duplicates, warnings, already_converted}
  const [convertForm, setConvertForm] = useState(null);
  const [convertLoading, setConvertLoading] = useState(false);
  const [convertSaving, setConvertSaving] = useState(false);
  const [convertedAppt, setConvertedAppt] = useState(null);
  const [centersList, setCentersList] = useState([]);
  const [advisorsList, setAdvisorsList] = useState([]);

  useEffect(() => {
    if (!canConvert) return;
    // Carga ligera de centros y asesores para el formulario de conversión
    (async () => {
      try {
        const [c, a] = await Promise.all([
          axiosAuth().get("/centers").catch(() => ({ data: [] })),
          axiosAuth().get("/advisors").catch(() => ({ data: [] })),
        ]);
        setCentersList(Array.isArray(c.data) ? c.data : []);
        setAdvisorsList(Array.isArray(a.data) ? a.data : []);
      } catch { /* ignore */ }
    })();
  }, [canConvert]); // eslint-disable-line

  const openConvert = async (lead) => {
    setConvertLead(lead);
    setConvertOpen(true);
    setConvertPrefill(null);
    setConvertedAppt(null);
    setConvertLoading(true);
    try {
      const res = await axiosAuth().get(`/admin/legacy-leads/${lead.id}/convert-prefill`);
      setConvertPrefill(res.data);
      setConvertForm({
        ...res.data.prefill,
        date: "",
        time_slot: "",
        center: "",
        center_name: res.data.prefill?.center_name || "",
        advisor_id: "",
        advisor_name: "",
        rental_vehicle_status: "",
        rental_vehicle_reserved: "",
        rental_pickup_time: "",
        rental_return_time: "",
        rental_internal_notes: "",
        replacement_vehicle_needed: "no",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar la conversión");
      setConvertOpen(false);
    } finally {
      setConvertLoading(false);
    }
  };

  const closeConvert = () => {
    setConvertOpen(false);
    setConvertLead(null);
    setConvertPrefill(null);
    setConvertForm(null);
    setConvertedAppt(null);
  };

  const submitConvert = async () => {
    if (!convertLead || !convertForm) return;
    if (!convertForm.date || !convertForm.time_slot) {
      toast.error("Fecha y hora son obligatorias.");
      return;
    }
    if (!convertForm.client_name?.trim()) {
      toast.error("El nombre del cliente es obligatorio.");
      return;
    }
    setConvertSaving(true);
    try {
      const res = await axiosAuth().post(
        `/admin/legacy-leads/${convertLead.id}/convert-to-appointment`,
        convertForm,
      );
      setConvertedAppt(res.data.appointment);
      toast.success("Cita creada desde lead histórico");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.appointment_id) {
        toast.error("Este lead ya fue convertido a una cita.");
        setConvertedAppt(d.appointment || { id: d.appointment_id });
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Error al convertir el lead"));
      }
    } finally {
      setConvertSaving(false);
    }
  };

  const buildParams = () => {
    const p = new URLSearchParams();
    if (q) p.append("q", q);
    if (phone) p.append("phone", phone);
    if (plate) p.append("plate", plate);
    if (formName) p.append("form_name", formName);
    if (consent !== "any") p.append("marketing_consent", consent);
    if (batchId) p.append("batch_id", batchId);
    p.append("page", page);
    p.append("limit", PAGE_SIZE);
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get(`/admin/legacy-leads?${buildParams().toString()}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando leads");
    } finally {
      setLoading(false);
    }
  };

  const loadMeta = async () => {
    try {
      const [f, b] = await Promise.all([
        axiosAuth().get("/admin/legacy-leads/forms"),
        axiosAuth().get("/admin/legacy-leads/batches"),
      ]);
      setForms(f.data.forms || []);
      setBatches(b.data.items || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);
  useEffect(() => { loadMeta(); /* eslint-disable-next-line */ }, []);

  // Deep-link: ?lead=<id> abre el detalle automáticamente (Iter 163)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const leadId = params.get("lead");
    if (!leadId) return;
    (async () => {
      try {
        const res = await axiosAuth().get(`/admin/legacy-leads/${leadId}`);
        if (res.data) setDetail(res.data);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line
  }, []);

  const applyFilters = () => { setPage(1); load(); };
  const clearFilters = () => {
    setQ(""); setPhone(""); setPlate(""); setFormName(""); setConsent("any"); setBatchId("");
    setPage(1); setTimeout(load, 0);
  };

  const openWizard = () => {
    setWizardOpen(true); setStep(1); setFile(null); setPreview(null);
    setImportResult(null); setConfirmText(""); setSampleOnly(true); setSampleSize(20);
  };

  const handlePreview = async () => {
    if (!file) { toast.error("Selecciona un CSV"); return; }
    if (!window.confirm("Vas a generar una vista previa. Esto NO importará nada todavía. ¿Continuamos?")) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axiosAuth().post("/admin/legacy-leads/import-preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error en vista previa");
    } finally {
      setImporting(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!file) return;
    if (confirmText !== "CONFIRMO") {
      toast.error("Escribe CONFIRMO para confirmar"); return;
    }
    if (!window.confirm(
      `Vas a importar ${sampleOnly ? `solo las primeras ${sampleSize} filas (prueba)` : "el CSV completo"}. ` +
      "Se guardarán en la colección legacy_leads, AISLADA del resto del sistema. ¿Continuar?"
    )) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("confirm", "CONFIRMO_IMPORTACION_LEADS");
      form.append("sample_only", sampleOnly ? "true" : "false");
      form.append("sample_size", String(sampleSize));
      const res = await axiosAuth().post("/admin/legacy-leads/import-confirm", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(res.data);
      setStep(3);
      load(); loadMeta();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error importando");
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await axiosAuth().get(`/admin/legacy-leads/export.xlsx?${buildParams().toString()}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `legacy_leads_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exportación descargada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error exportando");
    }
  };

  const handleDeleteBatch = async () => {
    if (!deleteConfirm) return;
    if (deleteText !== "ELIMINAR") { toast.error("Escribe ELIMINAR"); return; }
    try {
      const res = await axiosAuth().delete(
        `/admin/legacy-leads/batches/${deleteConfirm.id}?confirm=ELIMINAR`,
      );
      toast.success(`Borrado: ${res.data.deleted_leads} leads`);
      setDeleteConfirm(null); setDeleteText("");
      load(); loadMeta();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error borrando");
    }
  };

  const pages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  return (
    <div className="space-y-4" data-testid="legacy-leads-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase">Leads históricos</h1>
          <p className="text-sm text-slate-500 mt-1">
            Importación aislada de leads de la web anterior. <span className="text-emerald-700 font-semibold">No afecta a citas, KPIs, ni notificaciones actuales.</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setHistoryOpen(true)} variant="outline" className="rounded-sm text-xs uppercase tracking-wider" data-testid="legacy-leads-history-btn">
            <History className="w-3.5 h-3.5 mr-1.5" /> Importaciones
          </Button>
          <Button onClick={handleExport} variant="outline" className="rounded-sm text-xs uppercase tracking-wider" data-testid="legacy-leads-export-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Exportar Excel
          </Button>
          <Button onClick={openWizard} className="bg-slate-900 hover:bg-black text-white rounded-sm text-xs uppercase tracking-wider" data-testid="legacy-leads-import-btn">
            <Upload className="w-3.5 h-3.5 mr-1.5" /> Importar CSV
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre, email, comentarios..." className="rounded-sm" data-testid="ll-filter-q" />
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Teléfono" className="rounded-sm" data-testid="ll-filter-phone" />
            <Input value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="Matrícula" className="rounded-sm" data-testid="ll-filter-plate" />
            <Select value={formName || "__all"} onValueChange={(v) => setFormName(v === "__all" ? "" : v)}>
              <SelectTrigger className="rounded-sm" data-testid="ll-filter-form"><SelectValue placeholder="Formulario" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Todos los formularios</SelectItem>
                {forms.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={consent} onValueChange={setConsent}>
              <SelectTrigger className="rounded-sm" data-testid="ll-filter-consent"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Cualquier consentimiento</SelectItem>
                <SelectItem value="true">Solo con consentimiento</SelectItem>
                <SelectItem value="false">Sin consentimiento</SelectItem>
              </SelectContent>
            </Select>
            <Select value={batchId || "__all"} onValueChange={(v) => setBatchId(v === "__all" ? "" : v)}>
              <SelectTrigger className="rounded-sm" data-testid="ll-filter-batch"><SelectValue placeholder="Importación" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Todas las importaciones</SelectItem>
                {batches.map(b => (
                  <SelectItem key={b.id} value={b.id}>
                    {(b.filename || "?")} · {new Date(b.imported_at).toLocaleDateString("es-ES")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button onClick={applyFilters} className="bg-slate-900 text-white rounded-sm text-xs uppercase tracking-wider h-8" data-testid="ll-apply-filters"><Search className="w-3 h-3 mr-1.5" /> Buscar</Button>
            <Button onClick={clearFilters} variant="ghost" className="rounded-sm text-xs uppercase tracking-wider h-8" data-testid="ll-clear-filters"><X className="w-3 h-3 mr-1" /> Limpiar</Button>
            <p className="ml-auto text-xs text-slate-400 self-center" data-testid="ll-total">{total} resultados</p>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <p className="p-6 text-sm text-slate-400 text-center">Cargando...</p>
          ) : items.length === 0 ? (
            <div className="p-12 text-center" data-testid="ll-empty">
              <Database className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">No hay leads históricos para los filtros. Importa un CSV para empezar.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="p-2 text-left">Fecha</th>
                    <th className="p-2 text-left">Formulario</th>
                    <th className="p-2 text-left">Nombre</th>
                    <th className="p-2 text-left">Contacto</th>
                    <th className="p-2 text-left">Vehículo</th>
                    <th className="p-2 text-left">Consent.</th>
                    <th className="p-2 text-left"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map(it => (
                    <tr key={it.id} data-testid={`ll-row-${it.id}`} className="hover:bg-slate-50">
                      <td className="p-2 text-slate-600 whitespace-nowrap">
                        {it.original_date ? new Date(it.original_date).toLocaleDateString("es-ES") : "—"}
                      </td>
                      <td className="p-2">
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200 rounded-sm text-[10px]">{it.form_name || "—"}</Badge>
                      </td>
                      <td className="p-2 font-semibold text-slate-900">{it.name || "—"}</td>
                      <td className="p-2 text-slate-700">
                        <div>{it.email || <span className="text-slate-300">—</span>}</div>
                        <div className="text-slate-500">{it.phone || <span className="text-slate-300">—</span>}</div>
                      </td>
                      <td className="p-2 text-slate-700">
                        {it.plate && <div className="font-mono">{it.plate}</div>}
                        <div className="text-slate-500">{[it.brand, it.model].filter(Boolean).join(" ") || ""}</div>
                      </td>
                      <td className="p-2">
                        {it.marketing_consent ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 rounded-sm text-[10px]"><ShieldCheck className="w-2.5 h-2.5 mr-0.5" /> Sí</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-500 border-slate-200 rounded-sm text-[10px]">No</Badge>
                        )}
                      </td>
                      <td className="p-2">
                        <div className="flex gap-1 items-center">
                          <Button variant="ghost" size="sm" className="text-[10px] uppercase tracking-wider h-7 rounded-sm" onClick={() => setDetail(it)} data-testid={`ll-detail-${it.id}`}>
                            Ver
                          </Button>
                          {canConvert && (
                            it.converted_to_appointment_id ? (
                              <Link
                                to={`/admin/citas?id=${it.converted_to_appointment_id}`}
                                className="text-[10px] uppercase tracking-wider h-7 rounded-sm inline-flex items-center px-2 text-emerald-700 hover:bg-emerald-50"
                                data-testid={`ll-converted-link-${it.id}`}
                              >
                                <ExternalLink className="w-3 h-3 mr-1" /> Ver cita
                              </Link>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-[10px] uppercase tracking-wider h-7 rounded-sm text-blue-700 hover:bg-blue-50"
                                onClick={() => openConvert(it)}
                                data-testid={`ll-convert-${it.id}`}
                              >
                                <CalendarPlus className="w-3 h-3 mr-1" /> Convertir en cita
                              </Button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Paginación */}
      {pages > 1 && (
        <div className="flex justify-center gap-2" data-testid="ll-pagination">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-sm text-xs">‹ Anterior</Button>
          <span className="text-xs text-slate-500 self-center">Página {page} de {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="rounded-sm text-xs">Siguiente ›</Button>
        </div>
      )}

      {/* Wizard de importación */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="ll-wizard">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase">
              Importar Leads Históricos · Paso {step}/3
            </DialogTitle>
            <DialogDescription className="text-xs">
              {step === 1 && "Selecciona el CSV exportado de la web anterior."}
              {step === 2 && "Revisa la vista previa antes de confirmar."}
              {step === 3 && "Importación completada."}
            </DialogDescription>
          </DialogHeader>

          {step === 1 && (
            <div className="space-y-4 py-3">
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs">
                <AlertTriangle className="w-4 h-4 text-amber-700 inline mr-1.5" />
                <strong>Recomendación:</strong> en beta haz backup de la base de datos antes de importar. Los datos
                se guardan en una colección AISLADA. No tocan citas, KPIs ni clientes.
              </div>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="block w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-slate-900 file:text-white file:uppercase file:text-[10px] file:tracking-wider"
                  data-testid="ll-file-input"
                />
                {file && <p className="text-xs text-slate-600 mt-2">📄 {file.name} ({(file.size / 1024).toFixed(1)} KB)</p>}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setWizardOpen(false)} className="rounded-sm text-xs uppercase">Cancelar</Button>
                <Button onClick={handlePreview} disabled={!file || importing} className="bg-slate-900 text-white rounded-sm text-xs uppercase font-semibold" data-testid="ll-preview-btn">
                  {importing ? "Procesando..." : "Vista previa →"}
                </Button>
              </DialogFooter>
            </div>
          )}

          {step === 2 && preview && (
            <div className="space-y-3 py-3 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <KPI label="Filas detectadas" value={preview.total_rows} testid="kpi-rows" />
                <KPI label="Sin email" value={preview.rows_without_email} testid="kpi-no-email" />
                <KPI label="Sin teléfono" value={preview.rows_without_phone} testid="kpi-no-phone" />
                <KPI label="Duplicados en CSV" value={preview.duplicates_in_csv} testid="kpi-dups-csv" />
                <KPI label="Ya en BD (dups)" value={preview.duplicates_existing_in_db} testid="kpi-dups-db" />
                <KPI label="Filas con error" value={preview.rows_with_errors} testid="kpi-errors" />
                <KPI label="Desde" value={preview.date_range?.from ? preview.date_range.from.slice(0,10) : "—"} testid="kpi-from" />
                <KPI label="Hasta" value={preview.date_range?.to ? preview.date_range.to.slice(0,10) : "—"} testid="kpi-to" />
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-sm p-2">
                <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-1">Mapeo detectado</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-[10px]">
                  {Object.entries(preview.mapping || {}).map(([k, v]) => (
                    <div key={k} className="bg-white px-2 py-0.5 rounded-sm border">
                      <span className="text-slate-500">{k}</span> ← <span className="font-mono text-slate-800">{v}</span>
                    </div>
                  ))}
                </div>
                {preview.unmapped_columns?.length > 0 && (
                  <p className="text-[10px] text-slate-500 mt-1">Sin mapear: {preview.unmapped_columns.join(", ")}</p>
                )}
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-sm p-2">
                <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-1">Formularios detectados</p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(preview.forms_detected || {}).map(([f, n]) => (
                    <Badge key={f} className="bg-blue-100 text-blue-700 border-blue-200 rounded-sm text-[10px]">
                      {f}: {n}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="border border-slate-200 rounded-sm p-2 bg-amber-50">
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="checkbox" id="sample-only" checked={sampleOnly}
                    onChange={(e) => setSampleOnly(e.target.checked)}
                    data-testid="ll-sample-toggle"
                  />
                  <label htmlFor="sample-only" className="text-xs">Importar solo muestra de prueba</label>
                  {sampleOnly && (
                    <Input type="number" min={1} max={1000} value={sampleSize}
                            onChange={(e) => setSampleSize(parseInt(e.target.value) || 20)}
                            className="w-20 h-7 text-xs rounded-sm" data-testid="ll-sample-size" />
                  )}
                </div>
                <p className="text-[10px] text-amber-800 mt-1">
                  {sampleOnly
                    ? `Se importarán las primeras ${sampleSize} filas para validar.`
                    : `Se importarán las ${preview.total_rows} filas (puede tardar unos segundos).`}
                </p>
              </div>

              <div>
                <label className="text-xs">
                  Escribe <span className="font-mono bg-slate-100 px-1.5 py-0.5">CONFIRMO</span> para activar el botón de importación:
                </label>
                <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="rounded-sm h-9 mt-1" data-testid="ll-confirm-text" />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setStep(1)} className="rounded-sm text-xs uppercase">← Atrás</Button>
                <Button onClick={handleConfirmImport} disabled={confirmText !== "CONFIRMO" || importing}
                        className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm text-xs uppercase font-semibold disabled:bg-slate-300" data-testid="ll-confirm-import-btn">
                  {importing ? "Importando..." : (sampleOnly ? "Importar muestra" : "Importar todo")}
                </Button>
              </DialogFooter>
            </div>
          )}

          {step === 3 && importResult && (
            <div className="space-y-3 py-3">
              <div className="text-center py-2">
                <div className="w-12 h-12 mx-auto bg-emerald-100 rounded-sm flex items-center justify-center mb-2">
                  <ShieldCheck className="w-6 h-6 text-emerald-700" />
                </div>
                <h3 className="text-sm font-semibold uppercase tracking-wider">Importación completada</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <KPI label="Leídas" value={importResult.total_rows} testid="res-read" />
                <KPI label="Importadas" value={importResult.imported_count} testid="res-imported" />
                <KPI label="Duplicados" value={importResult.duplicate_count} testid="res-dup" />
                <KPI label="Errores" value={importResult.error_count} testid="res-err" />
              </div>
              <p className="text-[10px] text-slate-500">Batch ID: <span className="font-mono">{importResult.id}</span></p>
              <DialogFooter>
                <Button onClick={() => setWizardOpen(false)} className="bg-slate-900 text-white rounded-sm text-xs uppercase" data-testid="ll-wizard-close">Cerrar</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Historial */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl rounded-sm" data-testid="ll-history">
          <DialogHeader><DialogTitle className="heading-industrial text-lg uppercase">Importaciones anteriores</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-2 text-left">Fecha</th>
                  <th className="p-2 text-left">Archivo</th>
                  <th className="p-2 text-right">Importadas</th>
                  <th className="p-2 text-right">Duplicadas</th>
                  <th className="p-2 text-right">Errores</th>
                  <th className="p-2 text-left">Por</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {batches.length === 0 && (
                  <tr><td colSpan={7} className="p-6 text-center text-slate-400">Aún no hay importaciones.</td></tr>
                )}
                {batches.map(b => (
                  <tr key={b.id} data-testid={`batch-${b.id}`}>
                    <td className="p-2 text-slate-600">{new Date(b.imported_at).toLocaleString("es-ES")}</td>
                    <td className="p-2"><FileText className="w-3 h-3 inline mr-1" /> {b.filename}</td>
                    <td className="p-2 text-right font-semibold text-emerald-700">{b.imported_count}</td>
                    <td className="p-2 text-right text-slate-500">{b.duplicate_count}</td>
                    <td className="p-2 text-right text-red-500">{b.error_count}</td>
                    <td className="p-2 text-[10px] text-slate-500">{b.imported_by_email}</td>
                    <td className="p-2">
                      {isSuperAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(b)}
                                className="text-[10px] uppercase h-7 text-red-600 rounded-sm hover:bg-red-50"
                                data-testid={`delete-batch-${b.id}`}>
                          <Trash2 className="w-3 h-3 mr-1" /> Borrar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detalle */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[80vh] overflow-y-auto" data-testid="ll-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="heading-industrial text-lg uppercase">{detail.name || "Lead histórico"}</DialogTitle>
                <DialogDescription className="text-xs">
                  <Badge className="bg-blue-100 text-blue-700 border-blue-200 rounded-sm text-[10px] mr-1">{detail.form_name}</Badge>
                  {detail.original_date && new Date(detail.original_date).toLocaleString("es-ES")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-xs">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Email" value={detail.email} />
                  <Field label="Teléfono" value={detail.phone} />
                  <Field label="Matrícula" value={detail.plate} />
                  <Field label="Marca/Modelo" value={[detail.brand, detail.model].filter(Boolean).join(" ")} />
                  <Field label="KM" value={detail.kilometers} />
                  <Field label="Servicio" value={detail.service} />
                  <Field label="Centro" value={detail.center} />
                  <Field label="Estado original" value={detail.original_status} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Consentimiento comercial</p>
                  {detail.marketing_consent ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 rounded-sm">SÍ — apto para campañas</Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-700 border-red-200 rounded-sm">NO — no usar para marketing</Badge>
                  )}
                </div>
                {detail.comments && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">Comentarios</p>
                    <p className="bg-slate-50 p-2 rounded-sm border whitespace-pre-wrap">{detail.comments}</p>
                  </div>
                )}
                <details className="border border-slate-200 rounded-sm">
                  <summary className="bg-slate-50 px-2 py-1 text-[10px] uppercase tracking-wider text-slate-600 cursor-pointer">Fila original del CSV</summary>
                  <pre className="text-[10px] p-2 overflow-x-auto bg-white">{JSON.stringify(detail.original_row, null, 2)}</pre>
                </details>
                <p className="text-[10px] text-slate-400">Importado: {new Date(detail.imported_at).toLocaleString("es-ES")} · Batch <span className="font-mono">{detail.import_batch_id}</span></p>
                {canConvert && (
                  <div className="border-t pt-3 mt-2">
                    {detail.converted_to_appointment_id ? (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-2 flex items-center justify-between" data-testid="ll-detail-converted">
                        <div className="text-xs text-emerald-800">
                          <strong>Cita creada</strong> a partir de este lead
                          {detail.converted_at && (
                            <span className="text-[10px] text-emerald-600 ml-1">
                              · {new Date(detail.converted_at).toLocaleString("es-ES")}
                            </span>
                          )}
                          {detail.converted_by_email && (
                            <span className="text-[10px] text-emerald-600 ml-1">· {detail.converted_by_email}</span>
                          )}
                        </div>
                        <Link
                          to={`/admin/citas?id=${detail.converted_to_appointment_id}`}
                          className="text-[10px] uppercase tracking-wider inline-flex items-center px-2 py-1 rounded-sm bg-emerald-700 text-white hover:bg-emerald-800"
                          data-testid="ll-detail-converted-link"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" /> Ver cita
                        </Link>
                      </div>
                    ) : (
                      <Button
                        onClick={() => { setDetail(null); openConvert(detail); }}
                        className="bg-blue-700 hover:bg-blue-800 text-white rounded-sm text-xs uppercase tracking-wider w-full"
                        data-testid="ll-detail-convert-btn"
                      >
                        <CalendarPlus className="w-3.5 h-3.5 mr-1.5" /> Convertir en cita
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmar borrado */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => { setDeleteConfirm(null); setDeleteText(""); }}>
        <DialogContent className="max-w-sm rounded-sm border-red-200" data-testid="ll-delete-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase text-red-700">Borrar importación</DialogTitle>
            <DialogDescription>
              Vas a borrar {deleteConfirm?.imported_count} leads del batch <span className="font-mono">{deleteConfirm?.id?.slice(0,8)}</span>.
              Esta acción es irreversible y solo afecta a la colección <code>legacy_leads</code>.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs">Escribe <span className="font-mono bg-slate-100 px-1.5 py-0.5">ELIMINAR</span></label>
            <Input value={deleteText} onChange={(e) => setDeleteText(e.target.value)} className="rounded-sm h-9 mt-1" data-testid="ll-delete-text" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteConfirm(null); setDeleteText(""); }} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={handleDeleteBatch} disabled={deleteText !== "ELIMINAR"} className="bg-red-700 text-white rounded-sm text-xs uppercase font-semibold disabled:bg-slate-300" data-testid="ll-delete-confirm">
              Borrar definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conversión Lead → Cita (Iter 163) */}
      <Dialog open={convertOpen} onOpenChange={(o) => { if (!o) closeConvert(); }}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="ll-convert-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase">
              <CalendarPlus className="w-4 h-4 inline mr-1.5" /> Convertir en cita
            </DialogTitle>
            <DialogDescription className="text-xs">
              {convertLead && (
                <>Lead: <strong>{convertLead.name || "(sin nombre)"}</strong>
                  {convertLead.form_name && <> · {convertLead.form_name}</>}
                  {convertLead.original_date && <> · {convertLead.original_date.slice(0, 10)}</>}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {convertLoading && (
            <p className="text-xs text-slate-400 text-center py-6" data-testid="ll-convert-loading">Cargando datos del lead...</p>
          )}

          {convertedAppt && (
            <div className="space-y-3 py-3" data-testid="ll-convert-success">
              <div className="text-center py-2">
                <div className="w-12 h-12 mx-auto bg-emerald-100 rounded-sm flex items-center justify-center mb-2">
                  <ShieldCheck className="w-6 h-6 text-emerald-700" />
                </div>
                <h3 className="text-sm font-semibold uppercase tracking-wider">Cita creada</h3>
                <p className="text-xs text-slate-600 mt-1">
                  {convertedAppt.date} · {convertedAppt.time_slot} · {convertedAppt.client_name}
                </p>
                {convertedAppt.id && (
                  <p className="text-[10px] text-slate-400 mt-1">ID: <span className="font-mono">{convertedAppt.id}</span></p>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeConvert} className="rounded-sm text-xs uppercase" data-testid="ll-convert-close">Cerrar</Button>
                {convertedAppt.id && (
                  <Link
                    to={`/admin/citas?id=${convertedAppt.id}`}
                    onClick={closeConvert}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm text-xs uppercase tracking-wider inline-flex items-center px-3 py-2 font-semibold"
                    data-testid="ll-convert-go-appt"
                  >
                    <ExternalLink className="w-3 h-3 mr-1" /> Ir a la cita
                  </Link>
                )}
              </DialogFooter>
            </div>
          )}

          {!convertLoading && !convertedAppt && convertPrefill && convertForm && (
            <div className="space-y-3 py-3">
              {/* Avisos */}
              {convertPrefill.warnings?.length > 0 && (
                <div className="space-y-1.5" data-testid="ll-convert-warnings">
                  {convertPrefill.warnings.map((w, i) => (
                    <div key={i} className="bg-amber-50 border border-amber-200 rounded-sm p-2 text-xs flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-700 flex-shrink-0 mt-0.5" />
                      <span className="text-amber-900">{w}</span>
                    </div>
                  ))}
                </div>
              )}

              {convertPrefill.already_converted && (
                <div className="bg-red-50 border border-red-200 rounded-sm p-2 text-xs" data-testid="ll-convert-already">
                  <strong>Atención:</strong> este lead ya tiene una cita asociada
                  ({convertPrefill.already_converted.date} {convertPrefill.already_converted.time_slot}).
                  No se puede convertir de nuevo.
                </div>
              )}

              {convertPrefill.duplicates?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-2" data-testid="ll-convert-duplicates">
                  <p className="text-[10px] uppercase tracking-wider text-blue-800 font-semibold mb-1">
                    Posibles duplicados ({convertPrefill.duplicates.length}) — citas con misma matrícula o teléfono
                  </p>
                  <ul className="text-[11px] space-y-0.5 text-blue-900">
                    {convertPrefill.duplicates.slice(0, 6).map((d) => (
                      <li key={d.id} className="flex items-center gap-1.5">
                        <span className="font-mono">{d.date || "—"}</span>
                        <span className="text-blue-700">{d.time_slot}</span>
                        <span>·</span>
                        <span>{d.client_name || "—"}</span>
                        {d.plate && <span className="font-mono text-blue-700">[{d.plate}]</span>}
                        <span className="text-[10px] text-blue-600">({d.status})</span>
                        <Link to={`/admin/citas?id=${d.id}`} className="ml-auto text-[10px] underline text-blue-700">ver</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <fieldset className="border border-slate-200 rounded-sm p-2 space-y-2">
                <legend className="text-[10px] uppercase tracking-wider text-slate-500 px-1">Cliente</legend>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">Nombre *</label>
                    <Input value={convertForm.client_name || ""} onChange={(e) => setConvertForm({ ...convertForm, client_name: e.target.value })} className="rounded-sm h-8 text-xs" data-testid="ll-convert-name" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">Teléfono</label>
                    <Input value={convertForm.client_phone || ""} onChange={(e) => setConvertForm({ ...convertForm, client_phone: e.target.value })} className="rounded-sm h-8 text-xs" data-testid="ll-convert-phone" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">Email</label>
                    <Input value={convertForm.client_email || ""} onChange={(e) => setConvertForm({ ...convertForm, client_email: e.target.value })} className="rounded-sm h-8 text-xs" data-testid="ll-convert-email" />
                  </div>
                </div>
              </fieldset>

              <fieldset className="border border-slate-200 rounded-sm p-2 space-y-2">
                <legend className="text-[10px] uppercase tracking-wider text-slate-500 px-1">Vehículo</legend>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">Matrícula</label>
                    <Input value={convertForm.plate || ""} onChange={(e) => setConvertForm({ ...convertForm, plate: e.target.value.toUpperCase() })} className="rounded-sm h-8 text-xs font-mono" data-testid="ll-convert-plate" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">Marca</label>
                    <Input value={convertForm.brand || ""} onChange={(e) => setConvertForm({ ...convertForm, brand: e.target.value })} className="rounded-sm h-8 text-xs" data-testid="ll-convert-brand" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">Modelo</label>
                    <Input value={convertForm.model_name || ""} onChange={(e) => setConvertForm({ ...convertForm, model_name: e.target.value })} className="rounded-sm h-8 text-xs" data-testid="ll-convert-model" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">KM</label>
                    <Input value={convertForm.kilometers || ""} onChange={(e) => setConvertForm({ ...convertForm, kilometers: e.target.value })} className="rounded-sm h-8 text-xs" data-testid="ll-convert-km" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">Tipo vehículo</label>
                    <Input value={convertForm.vehicle_type || ""} onChange={(e) => setConvertForm({ ...convertForm, vehicle_type: e.target.value })} className="rounded-sm h-8 text-xs" data-testid="ll-convert-vtype" />
                  </div>
                </div>
              </fieldset>

              <fieldset className="border border-slate-200 rounded-sm p-2 space-y-2">
                <legend className="text-[10px] uppercase tracking-wider text-slate-500 px-1">Cita (a completar por recepción)</legend>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">Fecha *</label>
                    <Input type="date" value={convertForm.date || ""} onChange={(e) => setConvertForm({ ...convertForm, date: e.target.value })} className="rounded-sm h-8 text-xs" data-testid="ll-convert-date" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">Hora *</label>
                    <Input placeholder="09:00" value={convertForm.time_slot || ""} onChange={(e) => setConvertForm({ ...convertForm, time_slot: e.target.value })} className="rounded-sm h-8 text-xs" data-testid="ll-convert-time" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">Centro</label>
                    <Select value={convertForm.center || "__none"} onValueChange={(v) => {
                      if (v === "__none") setConvertForm({ ...convertForm, center: "", center_name: "" });
                      else {
                        const c = centersList.find(x => x.id === v);
                        setConvertForm({ ...convertForm, center: v, center_name: c?.name || "" });
                      }
                    }}>
                      <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="ll-convert-center"><SelectValue placeholder="Selecciona centro" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Sin asignar</SelectItem>
                        {centersList.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">Asesor</label>
                    <Select value={convertForm.advisor_id || "__none"} onValueChange={(v) => {
                      if (v === "__none") setConvertForm({ ...convertForm, advisor_id: "", advisor_name: "" });
                      else {
                        const a = advisorsList.find(x => x.id === v);
                        setConvertForm({ ...convertForm, advisor_id: v, advisor_name: a?.name || "" });
                      }
                    }}>
                      <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="ll-convert-advisor"><SelectValue placeholder="Selecciona asesor" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Sin asignar</SelectItem>
                        {advisorsList.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">Servicio</label>
                    <Input value={convertForm.service_type || ""} onChange={(e) => setConvertForm({ ...convertForm, service_type: e.target.value })} className="rounded-sm h-8 text-xs" data-testid="ll-convert-service" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">Notas internas</label>
                    <textarea value={convertForm.notes || ""} onChange={(e) => setConvertForm({ ...convertForm, notes: e.target.value })} rows={4} className="rounded-sm border border-slate-200 w-full text-xs p-2" data-testid="ll-convert-notes" />
                  </div>
                </div>
              </fieldset>

              <fieldset className="border border-slate-200 rounded-sm p-2 space-y-2">
                <legend className="text-[10px] uppercase tracking-wider text-slate-500 px-1">Vehículo de alquiler (opcional)</legend>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">¿Necesario?</label>
                    <Select value={convertForm.replacement_vehicle_needed || "no"} onValueChange={(v) => setConvertForm({ ...convertForm, replacement_vehicle_needed: v })}>
                      <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="ll-convert-rental-needed"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="yes">Sí</SelectItem>
                        <SelectItem value="unknown">Consultar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">Vehículo reservado</label>
                    <Input value={convertForm.rental_vehicle_reserved || ""} onChange={(e) => setConvertForm({ ...convertForm, rental_vehicle_reserved: e.target.value })} className="rounded-sm h-8 text-xs" data-testid="ll-convert-rental-reserved" />
                  </div>
                </div>
              </fieldset>

              <DialogFooter>
                <Button variant="outline" onClick={closeConvert} className="rounded-sm text-xs uppercase" data-testid="ll-convert-cancel">Cancelar</Button>
                <Button onClick={submitConvert} disabled={convertSaving || !!convertPrefill?.already_converted} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm text-xs uppercase font-semibold disabled:bg-slate-300" data-testid="ll-convert-submit">
                  {convertSaving ? "Creando..." : "Crear cita"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, testid }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm p-2" data-testid={testid}>
      <p className="text-[9px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-lg font-bold text-slate-900">{value ?? "—"}</p>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-slate-800">{value || <span className="text-slate-300">—</span>}</p>
    </div>
  );
}
